import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import SwiperAuto from './Swiper';
import { useHomeBanners } from '../hooks/useHomeBanners';
import React from 'react';

/**
 * Ideal banner sizes (px) – single source of truth for layout.
 * Desktop: main 55% × 360px, side slides 172px each (two stacked + gap = 360px).
 * Mobile: main 220px tall, side slides 160px each.
 */
export const BANNER_SIZES = {
    desktop: {
        containerHeight: 360,
        mainWidthPercent: 55,
        sideWidthPercent: 45,
        mainHeight: 360,
        sideSlideHeight: 172,
        gap: 8
    },
    mobile: {
        mainHeight: 220,
        sideSlideHeight: 160,
        gap: 8
    }
} as const;

/**
 * Recommended upload dimensions to show to users (e.g. in admin or docs).
 * Use these for best quality; images are displayed with object-fit: cover.
 * 2x values optional for retina; minimum = display size.
 */
export const RECOMMENDED_BANNER_DIMENSIONS = {
    mainHero: {
        label: 'Main hero (video or image)',
        desktop: '1200 × 720 px (min 600 × 360)',
        mobile: '800 × 440 px (min 400 × 220)',
        aspectRatio: '~5 : 3'
    },
    sideBanners: {
        label: 'Side banner slides (small carousel)',
        desktop: '800 × 344 px (min 400 × 172)',
        mobile: '800 × 320 px (min 400 × 160)',
        aspectRatio: '~5 : 2'
    }
} as const;

/** Short copy-paste text for admins / help */
export const BANNER_DIMENSIONS_HELP_TEXT = `
Recommended banner dimensions (for best display):

• Main hero (large banner):
  Desktop: 1200 × 720 px (or min 600 × 360)
  Mobile: 800 × 440 px (or min 400 × 220)
  Aspect ratio: 5 : 3

• Side banners (small carousel):
  Desktop: 800 × 344 px (or min 400 × 172)
  Mobile: 800 × 320 px (or min 400 × 160)
  Aspect ratio: 5 : 2

Images are cropped to fit (object-fit: cover). Use the aspect ratios to avoid important content being cut off.
`.trim();

const container = {
    height: {
        xs: 'auto',
        md: BANNER_SIZES.desktop.containerHeight
    },
    width: '100%',
    display: 'flex',
    flexDirection: {
        xs: 'column',
        md: 'row'
    },
    gap: { xs: 1, md: 0 }
};

const videoStyle = {
    width: {
        xs: '100%',
        md: `${BANNER_SIZES.desktop.mainWidthPercent}%`
    },
    height: {
        xs: BANNER_SIZES.mobile.mainHeight,
        md: BANNER_SIZES.desktop.mainHeight
    },
    minHeight: {
        xs: BANNER_SIZES.mobile.mainHeight,
        md: BANNER_SIZES.desktop.mainHeight
    },
    borderRadius: '24px',
    flexShrink: 0,
    objectFit: 'cover',
    objectPosition: 'center',
    position: 'relative'
};

const bannersContainer = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flexWrap: 'wrap',
    width: {
        xs: '100%',
        md: `${BANNER_SIZES.desktop.sideWidthPercent}%`
    },
    px: { xs: 0, md: 1 },
    py: { xs: 1, md: 0 },
    justifyContent: 'space-between',
    gap: { xs: 1, md: 0 },
    zIndex: 0
};

const BannersContainer = () => {
    const { data, isLoading, error } = useHomeBanners();

    if (error) {
        return <Box sx={container} />;
    }
    if (isLoading) {
        return (
            <Box sx={container}>
                <Skeleton variant="rectangular" sx={{ ...videoStyle, borderRadius: '24px' }} />
                <Box sx={{ ...bannersContainer, gap: 1 }}>
                    <Skeleton variant="rectangular" sx={{ height: { xs: BANNER_SIZES.mobile.sideSlideHeight, md: BANNER_SIZES.desktop.sideSlideHeight }, borderRadius: 1 }} />
                    <Skeleton variant="rectangular" sx={{ height: { xs: BANNER_SIZES.mobile.sideSlideHeight, md: BANNER_SIZES.desktop.sideSlideHeight }, borderRadius: 1 }} />
                </Box>
            </Box>
        );
    }
    const len = data?.length ? Math.ceil((data?.length - 1) / 2) : 1;
    const toSwiperCards = (items: typeof data) =>
        (items ?? []).map((item, idx) => ({ id: idx, fileName: item.fileName }));
    if (data?.[0]?.fileName !== undefined) {
        return (
            <Box sx={container}>
                <Box sx={videoStyle} component="video" src={data[0].fileName} muted autoPlay loop onEmptied={() => {}} />
                <Box sx={bannersContainer}>
                    <SwiperAuto cards={toSwiperCards(data.slice(1, len))} slideHeightMobile={BANNER_SIZES.mobile.sideSlideHeight} slideHeightDesktop={BANNER_SIZES.desktop.sideSlideHeight} />
                    <SwiperAuto cards={toSwiperCards(data.slice(len))} slideHeightMobile={BANNER_SIZES.mobile.sideSlideHeight} slideHeightDesktop={BANNER_SIZES.desktop.sideSlideHeight} />
                </Box>
            </Box>
        );
    }
    return <Box sx={container} />;
};

export default React.memo(BannersContainer);
